import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../config/api';
import { useToast } from '../../context/ToastContext';
import { useConfirmDialog } from '../../context/ConfirmContext';
import styles from '../../styles/modules/admin/AdminUsers.module.css';
import adminCommonStyles from '../../styles/modules/admin/AdminCommon.module.css';
import headerStyles from '../../styles/modules/Header.module.css';

interface User {
  _id: string;
  email: string;
  fullName: string;
  username: string;
  role: string;
  placeOfResidence?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
  phone?: string;
  userType: string;
  emailVerified?: boolean;
}

interface UserSection {
  staff: {
    users: User[];
    currentPage: number;
    totalPages: number;
    searchTerm: string;
    roleFilter: string;
    loading: boolean;
  };
  employers: {
    users: User[];
    currentPage: number;
    totalPages: number;
    searchTerm: string;
    roleFilter: string;
    loading: boolean;
  };
  admins: {
    users: User[];
    currentPage: number;
    totalPages: number;
    searchTerm: string;
    loading: boolean;
  };
}

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { confirmDelete } = useConfirmDialog();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  
  const [sections, setSections] = useState<UserSection>({
    staff: {
      users: [],
      currentPage: 1,
      totalPages: 1,
      searchTerm: '',
      roleFilter: 'all',
      loading: true,
    },
    employers: {
      users: [],
      currentPage: 1,
      totalPages: 1,
      searchTerm: '',
      roleFilter: 'all',
      loading: true,
    },
    admins: {
      users: [],
      currentPage: 1,
      totalPages: 1,
      searchTerm: '',
      loading: true,
    },
  });

  useEffect(() => {
    // Check if user is superadmin
    const userData = localStorage.getItem('user');
    if (userData) {
      const user = JSON.parse(userData);
      setIsSuperAdmin(user.role.toLowerCase() === 'superadmin');
    }
    
    fetchStaff();
    fetchEmployers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const fetchStaff = async () => {
    try {
      setSections(prev => ({
        ...prev,
        staff: { ...prev.staff, loading: true }
      }));
      
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: sections.staff.currentPage.toString(),
        limit: '20',
        ...(sections.staff.searchTerm && { search: sections.staff.searchTerm }),
        ...(sections.staff.roleFilter !== 'all' && { role: sections.staff.roleFilter }),
      });

      const response = await fetch(
        getApiUrl(`api/admin/staff?${params}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSections(prev => ({
          ...prev,
          staff: {
            ...prev.staff,
            users: result.data || [],
            totalPages: result.pagination?.totalPages || 1,
            loading: false,
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching staff:', error);
      setSections(prev => ({
        ...prev,
        staff: { ...prev.staff, loading: false }
      }));
    }
  };

  const fetchEmployers = async () => {
    try {
      setSections(prev => ({
        ...prev,
        employers: { ...prev.employers, loading: true }
      }));
      
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: sections.employers.currentPage.toString(),
        limit: '20',
        ...(sections.employers.searchTerm && { search: sections.employers.searchTerm }),
        ...(sections.employers.roleFilter !== 'all' && { role: sections.employers.roleFilter }),
      });

      const response = await fetch(
        getApiUrl(`api/admin/employers?${params}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSections(prev => ({
          ...prev,
          employers: {
            ...prev.employers,
            users: result.data || [],
            totalPages: result.pagination?.totalPages || 1,
            loading: false,
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching employers:', error);
      setSections(prev => ({
        ...prev,
        employers: { ...prev.employers, loading: false }
      }));
    }
  };

  const fetchAdmins = async () => {
    try {
      setSections(prev => ({
        ...prev,
        admins: { ...prev.admins, loading: true }
      }));
      
      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: sections.admins.currentPage.toString(),
        limit: '20',
        ...(sections.admins.searchTerm && { search: sections.admins.searchTerm }),
      });

      const response = await fetch(
        getApiUrl(`api/admin/admins?${params}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setSections(prev => ({
          ...prev,
          admins: {
            ...prev.admins,
            users: result.data || [],
            totalPages: result.pagination?.totalPages || 1,
            loading: false,
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching admins:', error);
      setSections(prev => ({
        ...prev,
        admins: { ...prev.admins, loading: false }
      }));
    }
  };

  useEffect(() => {
    fetchStaff();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.staff.currentPage, sections.staff.roleFilter, sections.staff.searchTerm]);

  useEffect(() => {
    fetchEmployers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.employers.currentPage, sections.employers.roleFilter, sections.employers.searchTerm]);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAdmins();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections.admins.currentPage, sections.admins.searchTerm, isSuperAdmin]);

  const handleStatusToggle = async (userId: string, currentStatus: boolean) => {
    try {
      const token = localStorage.getItem('authToken');
      const action = currentStatus ? 'suspend' : 'activate';
      
      const response = await fetch(
        getApiUrl(`api/admin/users/${userId}/${action}`),
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: currentStatus ? JSON.stringify({ reason: 'Suspended by admin' }) : undefined,
        }
      );

      if (response.ok) {
        showToast({ message: `User ${action}d successfully`, type: 'success' });
        // Refetch all sections to update the user status
        fetchStaff();
        fetchEmployers();
        if (isSuperAdmin) {
          fetchAdmins();
        }
      } else {
        const errorData = await response.json();
        showToast({ message: `Failed to ${action} user: ${errorData.message || 'Unknown error'}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error updating user status:', error);
      showToast({ message: 'An error occurred while updating user status', type: 'error' });
    }
  };

  const handleDeleteUser = async (userId: string) => {
    const confirmed = await confirmDelete({
      title: 'Delete user?',
      message: 'Are you sure you want to delete this user account?',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel'
    });
    if (!confirmed) {
      return;
    }

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        getApiUrl(`users/${userId}`),
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        showToast({ message: t('admin.users.deleteSuccess'), type: 'success' });
        // Refetch all sections to update after deletion
        fetchStaff();
        fetchEmployers();
        if (isSuperAdmin) {
          fetchAdmins();
        }
      } else {
        const errorData = await response.json();
        showToast({ message: `${t('admin.users.deleteFailed')}: ${errorData.message || t('messages.errorOccurred')}`, type: 'error' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      showToast({ message: t('admin.users.deleteFailed'), type: 'error' });
    }
  };

  const viewUserDetails = (user: User) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const getRoleBadgeClass = (role: string) => {
    const normalizedRole = role.toLowerCase().replace(/\s+/g, '');
    const roleClassMap: Record<string, string> = {
      staff: adminCommonStyles.roleStaff,
      employer: adminCommonStyles.roleEmployer,
      admin: adminCommonStyles.roleAdmin,
      superadmin: adminCommonStyles.roleSuperadmin,
    };

    return [adminCommonStyles.roleBadge, roleClassMap[normalizedRole]]
      .filter(Boolean)
      .join(' ');
  };

  const getStatusBadgeClass = (status: string) => {
    const normalizedStatus = status.toLowerCase();
    const statusClassMap: Record<string, string> = {
      active: adminCommonStyles.statusActive,
      suspended: adminCommonStyles.statusSuspended,
      inactive: adminCommonStyles.statusInactive,
      pending: adminCommonStyles.statusPending,
      available: adminCommonStyles.statusAvailable,
      open: adminCommonStyles.statusOpen,
      closed: adminCommonStyles.statusClosed,
      cancelled: adminCommonStyles.statusCancelled,
      booked: adminCommonStyles.statusBooked,
      expired: adminCommonStyles.statusExpired,
    };

    return [adminCommonStyles.statusBadge, statusClassMap[normalizedStatus]]
      .filter(Boolean)
      .join(' ');
  };

  const renderUserSection = (
    sectionType: 'staff' | 'employers' | 'admins',
    title: string,
    users: User[],
    currentPage: number,
    totalPages: number,
    searchTerm: string,
    loading: boolean,
    roleFilter?: string
  ) => {
    const updateSearch = (value: string) => {
      setSections(prev => ({
        ...prev,
        [sectionType]: { ...prev[sectionType], searchTerm: value, currentPage: 1 }
      }));
    };

    const updateRoleFilter = (value: string) => {
      setSections(prev => ({
        ...prev,
        [sectionType]: { ...prev[sectionType], roleFilter: value, currentPage: 1 }
      }));
    };

    const updatePage = (page: number) => {
      setSections(prev => ({
        ...prev,
        [sectionType]: { ...prev[sectionType], currentPage: page }
      }));
    };

    const roleOptions = sectionType === 'staff' 
      ? ['bartender', 'waiter', 'chef', 'cook', 'host/hostess']
      : ['venue_owner', 'manager'];

    return (
      <div className={styles.userSectionCard}>
        <div className={styles.sectionHeader}>
          <h2>{title}</h2>
          <span className={styles.userCount}>{users.length} {users.length === 1 ? 'user' : 'users'}</span>
        </div>

        <div className={styles.controlsSection}>
          <div className={styles.searchBox}>
            <input
              type="text"
              placeholder={`Search ${title.toLowerCase()}...`}
              value={searchTerm}
              onChange={(e) => updateSearch(e.target.value)}
              className={styles.searchInput}
            />
          </div>

          {roleFilter !== undefined && (
            <div className={styles.filters}>
              <select
                value={roleFilter}
                onChange={(e) => updateRoleFilter(e.target.value)}
                className={styles.filterSelect}
                aria-label="Filter by role"
              >
                <option value="all">All Roles</option>
                {roleOptions.map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        {loading ? (
          <div className={styles.sectionLoading}>Loading...</div>
        ) : (
          <>
            <div className={styles.tableContainer}>
              <table className={styles.usersTable}>
                <thead>
                  <tr>
                    <th>{t('admin.users.email')}</th>
                    <th>{t('admin.users.name')}</th>
                    <th>{t('admin.users.placeOfResidence')}</th>
                    <th>{t('admin.users.emailVerified')}</th>
                    <th>{t('admin.users.created')}</th>
                    <th>{t('admin.users.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user._id}>
                      <td>{user.email}</td>
                      <td>{user.fullName}</td>
                      <td>{user.placeOfResidence || 'N/A'}</td>
                      <td>{user.emailVerified ? '✓ Yes' : '✗ No'}</td>
                      <td>{new Date(user.createdAt).toLocaleDateString(t('common.locale') || 'en-US')}</td>
                      <td className={styles.actionsCell}>
                        <button
                          className={adminCommonStyles.btnView}
                          onClick={() => viewUserDetails(user)}
                          title={t('admin.users.viewDetails')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                            <circle cx="12" cy="12" r="3"/>
                          </svg>
                        </button>
                        <button
                          className={`${adminCommonStyles.btnToggle} ${user.isActive ? adminCommonStyles.btnSuspend : adminCommonStyles.btnActivate}`}
                          onClick={() => handleStatusToggle(user._id, user.isActive)}
                          title={user.isActive ? t('admin.users.suspend') : t('admin.users.activate')}
                        >
                          {user.isActive ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                              <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                          )}
                        </button>
                        <button
                          className={adminCommonStyles.btnDelete}
                          onClick={() => handleDeleteUser(user._id)}
                          title={t('admin.users.delete')}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {users.length === 0 && (
                <div className={styles.noResults}>
                  {t('admin.users.noResults')}
                </div>
              )}
            </div>

            <div className={styles.pagination}>
              <button
                className={styles.paginationBtn}
                onClick={() => updatePage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                ← {t('common.previous')}
              </button>
              <span className={styles.paginationInfo}>
                {t('common.pageOf', { current: currentPage, total: totalPages })}
              </span>
              <button
                className={styles.paginationBtn}
                onClick={() => updatePage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                {t('common.next')} →
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div className={styles.adminUsers}>
      <div className={styles.listPageHeader}>
        <button
          onClick={() => navigate('/admin')}
          className={headerStyles.backButton}
          aria-label={t('common.back')}
          title={t('common.back')}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
        </button>
        <h1 className={styles.headerTitle}>{t('admin.users.title')}</h1>
        <div></div>
      </div>

      <div className={styles.pageContent}>
        {/* Staff Section */}
        {renderUserSection(
          'staff',
          'Staff Members',
          sections.staff.users,
          sections.staff.currentPage,
          sections.staff.totalPages,
          sections.staff.searchTerm,
          sections.staff.loading,
          sections.staff.roleFilter
        )}

        {/* Employers Section */}
        {renderUserSection(
          'employers',
          'Employers',
          sections.employers.users,
          sections.employers.currentPage,
          sections.employers.totalPages,
          sections.employers.searchTerm,
          sections.employers.loading,
          sections.employers.roleFilter
        )}

        {/* Admins Section - Only for SuperAdmin */}
        {isSuperAdmin && renderUserSection(
          'admins',
          'Administrators',
          sections.admins.users,
          sections.admins.currentPage,
          sections.admins.totalPages,
          sections.admins.searchTerm,
          sections.admins.loading
        )}
      </div>

      {showModal && selectedUser && (
        <div className={adminCommonStyles.modalOverlay} onClick={() => setShowModal(false)}>
          <div className={adminCommonStyles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={adminCommonStyles.modalHeader}>
              <h2>{t('admin.users.userDetails')}</h2>
              <button className={adminCommonStyles.modalClose} onClick={() => setShowModal(false)}>
                ✕
              </button>
            </div>
            <div className={adminCommonStyles.modalBody}>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.email')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.email}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.name')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.fullName}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Username:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.username}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.phone')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.phone || 'N/A'}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.placeOfResidence')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.placeOfResidence || 'N/A'}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.emailVerified')}:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.emailVerified ? '✓ Yes' : '✗ No'}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>User Type:</span>
                <span className={adminCommonStyles.detailValue}>{selectedUser.userType}</span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.role')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  <span className={getRoleBadgeClass(selectedUser.role.toLowerCase())}>
                    {selectedUser.role}
                  </span>
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.status')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  <span className={getStatusBadgeClass(selectedUser.isActive ? 'active' : 'suspended')}>
                    {selectedUser.isActive ? t('admin.users.statusActive') : t('admin.users.statusSuspended')}
                  </span>
                </span>
              </div>
              <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>{t('admin.users.created')}:</span>
                <span className={adminCommonStyles.detailValue}>
                  {new Date(selectedUser.createdAt).toLocaleString()}
                </span>
              </div>
              {selectedUser.updatedAt && (
                <div className={`${adminCommonStyles.detailRow} ${styles.detailRow}`}>
                  <span className={`${adminCommonStyles.detailLabel} ${styles.detailLabel}`}>Last Updated:</span>
                  <span className={adminCommonStyles.detailValue}>
                    {new Date(selectedUser.updatedAt).toLocaleString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
